// Term Loan Payment verification (legacy /AccountsApproval/VerifyTermloanPayment).
// Loan repayments (principal + interest) paid from a bank; verified/approved via the shared status
// list (legacy drops "Return" here). Payments returned earlier (status '0') are corrected on the
// Term Loan Payment entry screen, as in legacy.
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Receipt, Clock, Building2, RotateCcw } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyTLPaymentQueue,
    fetchVerifyTLPaymentDetail,
    submitVerifyTLPayment,
    clearVerifyTLPaymentDetail,
    resetVerifyTLPayment,
    selectVTPQueue, selectVTPQueueLoading, selectVTPQueueError,
    selectVTPDetail, selectVTPDetailKey, selectVTPDetailLoading, selectVTPDetailError,
    selectVTPVerifyLoading,
} from '../../slices/accountsSlice/verifyTLPaymentSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Reject: 'Rejected Successfully',
};

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);

const VerifyTLPayment = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVTPQueue);
    const queueLoading = useSelector(selectVTPQueueLoading);
    const queueError = useSelector(selectVTPQueueError);
    const detail = useSelector(selectVTPDetail);
    const detailKey = useSelector(selectVTPDetailKey);
    const detailLoading = useSelector(selectVTPDetailLoading);
    const detailError = useSelector(selectVTPDetailError);
    const verifyLoading = useSelector(selectVTPVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.paymentstatus) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const payment = selectedItem && detailKey === selectedItem.PaymentTransactionNo ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyTLPaymentQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy removes "Return" from this page's status dropdown.
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetVerifyTLPayment());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyTLPaymentDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('No'));
        if (String(selectedItem.paymentstatus) !== '0') dispatch(fetchVerifyTLPaymentDetail(selectedItem.PaymentTransactionNo));
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID; the detail does. Remarks are keyed on the loan transaction no (TNO).
    useEffect(() => {
        if (!payment?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: payment.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(payment.MOID));
        dispatch(fetchRemarks({ trno: payment.TNO, moid: payment.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payment?.MOID, payment?.TNO]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyTLPaymentQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyTLPaymentQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyTLPaymentDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!payment) { toast.error('No term loan payment selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const payload = {
            TNO: payment.TNO,
            Apprstatus: actionValue,
            ApprovalRemarks: comment.trim(),
            Createdby: getCurrentUser(),
            RoleId: roleId,
        };

        try {
            const result = await dispatch(submitVerifyTLPayment(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Term Loan Payment ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            // e.g. "Insufficent Balance" from spVerifyTermloanPayment
            toast.error(status || 'Error Occurred While Verification');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Verification');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return String(item.PaymentTransactionNo ?? '').toLowerCase().includes(q)
            || item.BankName?.toLowerCase().includes(q)
            || item.BDate?.toLowerCase().includes(q)
            || String(item.TotalAmount ?? '').includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">Txn {item.PaymentTransactionNo}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.BDate}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.TotalAmount)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1 min-w-0"><Building2 className="w-3 h-3 flex-shrink-0" /><span className="truncate">{item.BankName}</span></span>
                <StatusBadge status={item.paymentstatus} />
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.PaymentTransactionNo}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.BDate}</span>
            <span className="font-semibold text-gray-900 dark:text-white truncate max-w-[260px]">{item.BankName}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.TotalAmount)}</span>
            <span className="ml-auto"><StatusBadge status={item.paymentstatus} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Receipt className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Payment Type" value={payment.PaymentType} />
                <Field label="Loan No" value={payment.LoanNos} />
                <Field label="Agency" value={payment.Acodeandname} />
                <Field label="Payment For" value={payment.paymentstatus} />
                <Field label="Principal Amount" value={formatAmount(payment.Principleamt)} />
                <Field label="Interest Amount" value={formatAmount(payment.IntrestAmt)} />
                <Field label="Installment No" value={payment.instno} />
                <Field label="Balance Principal" value={formatAmount(payment.BalPricipleamt)} />
                <Field label="Remarks" value={payment.TLPRemarks} className="sm:col-span-2 lg:col-span-4" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Field label="Bank Name" value={payment.BankName} />
                <Field label="Mode of Pay" value={payment.Modeofpay} />
                <Field label="Instrument No" value={payment.Instrumentno} />
                <Field label="Payment Date" value={payment.BDate} />
                <Field label="Total Amount" value={formatAmount(payment.TotalAmount)} />
                <Field label="Amount in Words" value={payment.AmountInWords} className="sm:col-span-3" />
                <Field label="Bank Remarks" value={payment.BankRemarks} className="sm:col-span-2 lg:col-span-4" />
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this payment</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (isReturned) {
            return (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border-2 border-amber-200 dark:border-amber-700 flex items-start gap-3">
                    <RotateCcw className="w-5 h-5 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Payment {selectedItem.PaymentTransactionNo} was returned for update</h3>
                        <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">Correct and resubmit it from the Term Loan Payment entry screen — there is nothing to verify until it is resubmitted.</p>
                    </div>
                </div>
            );
        }
        if (detailLoading || (!payment && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading payment details…</span>
                </div>
            );
        }
        if (detailError || !payment) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Payment details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Loan {payment.LoanNos} — Repayment</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{formatAmount(payment.TotalAmount)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {payment.BankName}
                        </span>
                        {payment.PaymentType && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">{payment.PaymentType}</span>
                        )}
                        <StatusBadge status={selectedItem.paymentstatus} />
                    </div>
                </div>

                {renderDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Notes"
                    placeholder="Enter verification notes…"
                    required
                    rows={3}
                    showCharCount
                />
                {renderActions()}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Term Loan Payment Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Receipt}
                badgeText="TL Payment Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by transaction no, bank, date, amount…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No term loan payments pending verification!',
                        itemKey: 'PaymentTransactionNo',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Term Loan Payment Verification',
                        icon: Receipt,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Payment Details',
                        icon: Receipt,
                        selectedTitle: 'Term Loan Payment Verification',
                        emptyTitle: 'No Payment Selected',
                        emptyMessage: 'Select a payment from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyTLPayment;
